import { useState, useEffect } from 'react';
import { Routes, Route, useNavigate } from 'react-router-dom';
import { Menu, X, Home, BookOpen, Target, Trophy, Video, MessageSquare, User, Settings, Bell } from 'lucide-react';
import axios from 'axios';
import { API } from '../App';
import notificationManager from '../utils/notificationManager';
import { toast } from 'sonner';
import HomePage from '../components/student/HomePage';
import ClassRecords from '../components/student/ClassRecords';
import DailyTasks from '../components/student/DailyTasks';
import Leaderboard from '../components/student/Leaderboard';
import Videos from '../components/student/Videos';
import Questions from '../components/student/Questions';
import Profile from '../components/student/Profile';
import SettingsPage from '../components/student/SettingsPage';
import AddAward from '../components/student/AddAward';
import NotificationInbox from '../components/student/NotificationInbox';

export default function StudentDashboard({ user, setUser }) {
  const [sidebarOpen, setSidebarOpen] = useState(window.innerWidth >= 768);
  const [unreadCount, setUnreadCount] = useState(0);
  const [notificationPermissionRequested, setNotificationPermissionRequested] = useState(false);
  const navigate = useNavigate();

  // Load unread notification count
  useEffect(() => {
    loadUnreadCount();
    // Poll every 30 seconds
    const interval = setInterval(loadUnreadCount, 30000);
    return () => clearInterval(interval);
  }, []);

  // Initialize push notifications
  useEffect(() => {
    const initPushNotifications = async () => {
      try {
        // 檢查瀏覽器支持
        if (!notificationManager.isSupported()) {
          console.log('瀏覽器不支持推送通知');
          return;
        }

        // 如果已經授權，直接開始輪詢
        if (notificationManager.getPermission() === 'granted') {
          startNotificationPolling();
          return;
        }

        // 等待用戶進入頁面一段時間後再請求權限（更友好）
        setTimeout(async () => {
          if (!notificationPermissionRequested) {
            try {
              const granted = await notificationManager.requestPermission();
              setNotificationPermissionRequested(true);
              
              if (granted) {
                toast.success('推送通知已啟用');
                startNotificationPolling();
              } else {
                toast.info('您可以在設定中開啟推送通知');
              }
            } catch (error) {
              console.error('請求通知權限失敗:', error);
              setNotificationPermissionRequested(true);
            }
          }
        }, 3000); // 延遲3秒後請求
      } catch (error) {
        console.error('初始化推送通知失敗:', error);
      }
    };

    const startNotificationPolling = () => {
      try {
        const token = localStorage.getItem('token');
        if (token) {
          notificationManager.startPolling(
            API,
            token,
            (newNotifications) => {
              // 當收到新通知時更新未讀數
              loadUnreadCount();
            }
          );
        }
      } catch (error) {
        console.error('啟動通知輪詢失敗:', error);
      }
    };

    initPushNotifications();

    // 清理函數
    return () => {
      try {
        notificationManager.stopPolling();
      } catch (error) {
        console.error('停止通知輪詢失敗:', error);
      }
    };
  }, [notificationPermissionRequested]);

  const loadUnreadCount = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API}/notifications/unread-count`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      setUnreadCount(res.data.count);
    } catch (error) {
      console.error('載入未讀數失敗:', error);
    }
  };

  const menuItems = [
    { name: '主頁', icon: Home, path: '/student/home', testid: 'nav-home' },
    { name: '課堂紀錄', icon: BookOpen, path: '/student/records', testid: 'nav-records' },
    { name: '每日任務', icon: Target, path: '/student/tasks', testid: 'nav-tasks' },
    { name: '排行榜', icon: Trophy, path: '/student/leaderboard', testid: 'nav-leaderboard' },
    { name: '小學堂', icon: Video, path: '/student/videos', testid: 'nav-videos' },
    { name: '技術提問', icon: MessageSquare, path: '/student/questions', testid: 'nav-questions' },
    { name: '通知收件夾', icon: Bell, path: '/student/notifications', testid: 'nav-notifications' },
    { name: '我的帳戶', icon: User, path: '/student/profile', testid: 'nav-profile' },
    { name: '設定', icon: Settings, path: '/student/settings', testid: 'nav-settings' }
  ];

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('currentOrg');
    setUser(null);
    navigate('/');
  };

  return (
    <div className="flex h-screen bg-gradient-to-br from-orange-50 to-teal-50">
      {/* Sidebar */}
      <div className={`${sidebarOpen ? 'w-64' : 'w-0'} md:w-64 bg-white shadow-xl transition-all duration-300 flex flex-col overflow-hidden fixed md:relative h-full z-30`}>
        <div className="p-4 flex items-center justify-between border-b">
          <h2 className="text-lg md:text-xl font-bold text-gray-800">學習系統</h2>
          <button 
            data-testid="toggle-sidebar-btn"
            onClick={() => setSidebarOpen(!sidebarOpen)}
            className="p-2 hover:bg-gray-100 rounded-lg md:hidden"
          >
            <X size={20} />
          </button>
        </div>

        <nav className="flex-1 p-4 space-y-2 overflow-y-auto">
          {menuItems.map((item) => (
            <button
              key={item.path}
              data-testid={item.testid}
              onClick={() => {
                navigate(item.path);
                if (window.innerWidth < 768) setSidebarOpen(false);
                // Reload unread count when navigating to notifications
                if (item.path === '/student/notifications') {
                  setTimeout(loadUnreadCount, 500);
                }
              }}
              className="w-full flex items-center gap-3 p-3 rounded-lg hover:bg-orange-100 transition-colors text-gray-700 relative"
            >
              <item.icon size={20} />
              <span className="text-sm md:text-base">{item.name}</span>
              {/* Unread badge for notifications */}
              {item.path === '/student/notifications' && unreadCount > 0 && (
                <span className="ml-auto bg-red-500 text-white text-xs font-bold rounded-full px-2 py-1 min-w-[24px] text-center">
                  {unreadCount > 99 ? '99+' : unreadCount}
                </span>
              )}
            </button>
          ))}
        </nav>

        <div className="p-4 border-t">
          <button
            data-testid="logout-btn"
            onClick={handleLogout}
            className="w-full p-3 bg-red-500 text-white rounded-lg hover:bg-red-600 transition-colors text-sm md:text-base"
          >
            登出
          </button>
        </div>
      </div>

      {/* Mobile overlay */}
      {sidebarOpen && (
        <div 
          className="fixed inset-0 bg-black/50 z-20 md:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Main Content */}
      <div className="flex-1 overflow-y-auto">
        {/* Mobile menu button - Always visible on mobile */}
        <button
          onClick={() => setSidebarOpen(true)}
          className="md:hidden fixed top-4 left-4 bg-orange-500 text-white p-3 rounded-lg shadow-lg z-10 relative"
        >
          <Menu size={24} />
          {/* Unread badge on mobile menu button */}
          {unreadCount > 0 && (
            <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs font-bold rounded-full w-6 h-6 flex items-center justify-center border-2 border-white">
              {unreadCount > 99 ? '99+' : unreadCount}
            </span>
          )}
        </button>
        <Routes>
          <Route path="home" element={<HomePage user={user} />} />
          <Route path="records" element={<ClassRecords user={user} />} />
          <Route path="tasks" element={<DailyTasks user={user} />} />
          <Route path="leaderboard" element={<Leaderboard user={user} />} />
          <Route path="videos" element={<Videos user={user} />} />
          <Route path="questions" element={<Questions user={user} />} />
          <Route path="notifications" element={<NotificationInbox user={user} />} />
          <Route path="profile" element={<Profile user={user} />} />
          <Route path="add-award" element={<AddAward user={user} />} />
          <Route path="settings" element={<SettingsPage user={user} />} />
        </Routes>
      </div>
    </div>
  );
}
